import React, { useContext, useEffect, useState } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { Table } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import { CoolFarmOptionsContext } from '../../index'
import { useTranslation } from 'react-i18next'
import i18n from '~/components/i18n'
import InfoPopup from '~/components/InfoPopup'
import GrazingFertilizers from './GrazinFertilizers'

const getTableColumnHeaders = () => [
  {
    name: i18n.t('Category'),
    type: 'category'
  },
  {
    name: i18n.t('Days'),
    key: 'grazingDays',
    type: 'input',
    info: i18n.t('Number of days during reference year that animals in this category are grazing (between 0 and 365)'),
    max: 365
  },
  {
    name: i18n.t('Hours / day'),
    key: 'grazingHours',
    max: 24,
    type: 'input',
    info: i18n.t('Average number of hours grazing per day during the reference period')
  },
  {
    name: i18n.t('Grazing type'),
    key: 'grazingType',
    selectKey: 'grazingTypes',
    type: 'select',
    info: i18n.t('Grazing type information is needed for the energy requirement calculation. Confined pasture refers to grazing where animals are confined in areas with sufficient forage requiring modest energy expense to acquire feed. Rangeland / rough grazingrefers to animals grazing in open range land or hilly terrain and expend')
  },
  {
    name: i18n.t('Grazing quality'),
    key: 'grazingQuality',
    selectKey: 'grazingQualities',
    type: 'select',
    info: i18n.t('Grazing quality information is required for the digestibility of grass, high quality refers to grass from good pastures (generally fertilized), low quality refers to grass from rangelands (mostly unfertilized)')
  }
]

const Grazing: React.FC = () => {
  const { t } = useTranslation()
  const { control } = useFormContext()
  const options = useContext(CoolFarmOptionsContext)
  
  const watchHerdGroups = useWatch({ control, name: 'herdGroups' })
  const checkedHerdGroups = watchHerdGroups?.filter(herd => herd.herdCategoryId) || []

  const [tableColumnHeaders, setTableColumnHeaders] = useState(getTableColumnHeaders())

  useEffect(() => {
    setTableColumnHeaders(tableColumnHeaders)
  }, [t])

  const herdCategoryOptions = options?.herdCategories?.map(category => ({
    key: category.id,
    value: category.id,
    text: category.label
  })) || []

  function getHerdCategoryLabel(id) {
    const herdOption = herdCategoryOptions.find(herd => herd.value === id)
    if(herdOption) {
      return herdOption.text
    }
    return ''
  }

  function renderCellValue(column, herdGroup, herdIndex) {
    switch(column.type) {
      case 'category':
        return getHerdCategoryLabel(herdGroup.herdCategoryId)
      case 'select':
        return <FormFields.Select
          control={control}
          name={`herdGroups[${herdIndex}].${column.key}`}
          placeholder={t('Select')}
          rules={{ required: true }}
          options={options[column.selectKey]?.map(unit => ({
            key: unit.key,
            value: unit.key,
            text: unit.label
          })) || []}
          withoutAsterisk
          noErrorMessage
        />
      case 'input':
        return <FormFields.Input
          control={control}
          name={`herdGroups[${herdIndex}].${column.key}`}
          placeholder='0'
          typeInput='controled'
          rules={{
            required: true,
            max: column.max
          }}
          withoutAsterisk
          noErrorMessage
        />
    }
  }

  function renderTableBody() {
    return checkedHerdGroups.map((herdGroup, herdIndex) => {
      return <Table.Row key={herdGroup.herdCategoryId}>
        {tableColumnHeaders.map(column => {
          return <Table.Cell key={`${herdGroup.herdCategoryId}-${column.type}`}>{renderCellValue(column, herdGroup, herdIndex)}</Table.Cell>
        })}
      </Table.Row>
    })
  }

  function renderGrazing() {

    return <Table celled structured>
      <Table.Header>
        <Table.Row>
          {tableColumnHeaders.map(column => {
            return <Table.HeaderCell key={column.key}>
              <div style={{ display: 'flex', gap: 5 }}>
                <div>{column.name}</div>
                {column.info && <InfoPopup
                  content={column.info}
                  styleIcon={{ fontSize: '1.2em', color: 'white' }}
                />}
              </div>
            </Table.HeaderCell>
          })}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {renderTableBody()}
      </Table.Body>
    </Table>
  }

  return (
    <div>
      {renderGrazing()}
      <GrazingFertilizers />
    </div>
  )
}

export default Grazing
